import { useQuery } from "@tanstack/react-query";
import { AlertCircle, DollarSign, TrendingDown, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/utils/trpc";

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(amount);
}

function formatFrequency(frequency: string) {
  const map: Record<string, string> = {
    daily: "Diário",
    weekly: "Semanal",
    biweekly: "Quinzenal",
    monthly: "Mensal",
    quarterly: "Trimestral",
    yearly: "Anual",
  };
  return map[frequency] || frequency;
}

export function FixedExpensesManager() {
  // Fetch fixed expenses
  const fixedExpenses = useQuery(trpc.recurring.listFixed.queryOptions());

  // Fetch daily budget data to get total income
  const dailyBudget = useQuery(trpc.budgets.getDailyBudget.queryOptions());

  // Loading state
  if (fixedExpenses.isLoading || dailyBudget.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-muted-foreground">Carregando gastos fixos...</div>
      </div>
    );
  }

  // Error state
  if (fixedExpenses.isError || dailyBudget.isError) {
    return (
      <Card className="border-red-200">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-5 w-5" />
            <span>Erro ao carregar gastos fixos</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const data = fixedExpenses.data;
  const totalExpenses = data?.totalExpenses || 0;
  const totalIncome = data?.totalIncome || 0;
  const netExpenses = data?.total || 0;

  // Get monthly income from daily budget calculation
  const monthlyIncome = dailyBudget.data?.totalIncome || 0;
  const balance = monthlyIncome - totalExpenses;
  const isDeficit = balance < 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Total Fixed Expenses */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">Total Gastos Fixos</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl text-red-600">
              {formatCurrency(totalExpenses)}
            </div>
            <p className="text-muted-foreground text-xs">
              {data?.fixedExpenses.length} gastos fixos mensais
            </p>
          </CardContent>
        </Card>

        {/* Monthly Income */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">Renda Mensal</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="font-bold text-2xl text-green-600">
              {formatCurrency(monthlyIncome)}
            </div>
            <p className="text-muted-foreground text-xs">
              Renda líquida (após impostos)
            </p>
          </CardContent>
        </Card>

        {/* Balance */}
        <Card className={isDeficit ? "border-red-200 bg-red-50/50" : "border-green-200 bg-green-50/50"}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="font-medium text-sm">
              {isDeficit ? "Déficit" : "Saldo Disponível"}
            </CardTitle>
            <DollarSign className={`h-4 w-4 ${isDeficit ? "text-red-600" : "text-green-600"}`} />
          </CardHeader>
          <CardContent>
            <div className={`font-bold text-2xl ${isDeficit ? "text-red-600" : "text-green-600"}`}>
              {formatCurrency(Math.abs(balance))}
            </div>
            <p className="text-muted-foreground text-xs">
              {isDeficit
                ? "⚠️ Gastos maiores que a renda"
                : "✅ Disponível após gastos fixos"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Alert if deficit */}
      {isDeficit && (
        <Card className="border-red-200 bg-red-50/50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-red-900">Atenção: Déficit Detectado</h3>
                <p className="text-red-700 text-sm mt-1">
                  Seus gastos fixos (R$ {totalExpenses.toFixed(2)}) são maiores que sua renda mensal
                  (R$ {monthlyIncome.toFixed(2)}). Você está com um déficit de R$ {Math.abs(balance).toFixed(2)} por mês.
                  Considere revisar seus gastos fixos ou aumentar sua renda.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fixed Expenses by Category */}
      {data?.byCategory && Object.keys(data.byCategory).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Gastos por Categoria</CardTitle>
            <CardDescription>
              Agrupamento dos gastos fixos por categoria
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(data.byCategory).map(([categoryName, categoryData]) => {
                const percentage = (categoryData.total / totalExpenses) * 100;
                return (
                  <div key={categoryName} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{categoryName}</span>
                        <Badge variant="outline" className="text-xs">
                          {categoryData.count} {categoryData.count === 1 ? "item" : "itens"}
                        </Badge>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{formatCurrency(categoryData.total)}</div>
                        <div className="text-muted-foreground text-xs">{percentage.toFixed(1)}%</div>
                      </div>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-200">
                      <div
                        className="h-2 rounded-full bg-blue-500"
                        style={{ width: `${Math.min(percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Fixed Expenses Table */}
      <Card>
        <CardHeader>
          <CardTitle>Todos os Gastos Fixos</CardTitle>
          <CardDescription>
            Lista completa de transações recorrentes marcadas como gastos fixos
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data?.fixedExpenses.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              Nenhum gasto fixo cadastrado. Configure suas transações recorrentes como "Gasto Fixo".
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Descrição</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Frequência</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.fixedExpenses.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell className="font-medium">
                      {expense.description}
                    </TableCell>
                    <TableCell>
                      {expense.category ? (
                        <Badge variant="outline">
                          {expense.category.icon} {expense.category.name}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">
                          Sem categoria
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatFrequency(expense.frequency)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      <span className={expense.type === "credit" ? "text-green-600" : "text-red-600"}>
                        {expense.type === "credit" ? "+" : "-"}
                        {formatCurrency(Number(expense.amount))}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
